import { computed, ref, watch } from "vue";
import { defineStore } from "pinia";
import {
  getConvexAuthInfo,
  getProjectsConvexClient,
  projectFns,
  recoverProjectsConvexSubscription,
  runProjectsConvexMutation,
  runProjectsConvexQuery,
} from "@/v2Projects/convexClient";
import { captureConvexError } from "@/v2Projects/telemetry";
import { buildProjectUserError } from "@/v2Projects/userErrors";
import { useAuthStore } from "@/store/auth";
import type { DashboardNotification } from "@/v2Projects/types";

const PAGE_SIZE = 20;

interface RetainNotificationsOptions {
  subscribeToUnreadCount?: boolean;
}

function toViewerKey(authInfo: ReturnType<typeof getConvexAuthInfo>): string | null {
  if (!authInfo) {
    return null;
  }

  return `${authInfo.orgId}:${authInfo.userId}`;
}

export const useProjectNotificationsStore = defineStore("v2ProjectNotifications", () => {
  const latestPage = ref<DashboardNotification[]>([]);
  const olderPages = ref<DashboardNotification[][]>([]);
  const continueCursor = ref<string | null>(null);
  const isDone = ref(false);
  const isReady = ref(false);
  const isLoadingMore = ref(false);
  const loadError = ref<string | null>(null);
  const serverUnreadCount = ref(0);
  /** Recipients being dismissed; subtracted from the badge until the server no longer lists them. */
  const dismissingRecipientIds = ref(new Set<DashboardNotification["recipient_id"]>());
  const unreadCount = computed(() => {
    // Only unread entries are part of the server count, so only they may be subtracted.
    const listedUnread = new Set(
      notifications.value.filter((notification) => !notification.is_read).map((notification) => notification.recipient_id),
    );
    let pending = 0;
    dismissingRecipientIds.value.forEach((recipientId) => { if (listedUnread.has(recipientId)) pending += 1; });
    return Math.max(0, serverUnreadCount.value - pending);
  });
  const currentViewerKey = ref<string | null>(toViewerKey(getConvexAuthInfo()));
  const isEnabled = ref(currentViewerKey.value !== null);
  const listVersion = ref(0);

  let unsubscribeList: (() => void) | null = null;
  let unsubscribeCount: (() => void) | null = null;
  let unwatchAuthStore: (() => void) | null = null;
  let authCheckInterval: ReturnType<typeof setInterval> | null = null;
  let listRecoveryAttempts = 0;
  let countRecoveryAttempts = 0;
  let consumerCount = 0;
  let unreadCountConsumerCount = 0;

  const notifications = computed(() => [...latestPage.value, ...olderPages.value.flat()]);
  const hasMore = computed(() => !isDone.value);

  function canUseNotifications(): boolean {
    return isEnabled.value;
  }

  function cleanupListSubscription() {
    if (unsubscribeList) {
      unsubscribeList();
      unsubscribeList = null;
    }
  }

  function cleanupCountSubscription() {
    if (unsubscribeCount) {
      unsubscribeCount();
      unsubscribeCount = null;
    }
  }

  function cleanupSubscriptions() {
    cleanupListSubscription();
    cleanupCountSubscription();
  }

  function cleanup() {
    cleanupSubscriptions();
    if (unwatchAuthStore) {
      unwatchAuthStore();
      unwatchAuthStore = null;
    }
    if (authCheckInterval) {
      clearInterval(authCheckInterval);
      authCheckInterval = null;
    }
  }

  function resetPages() {
    latestPage.value = [];
    olderPages.value = [];
    continueCursor.value = null;
    isDone.value = false;
    isReady.value = !canUseNotifications();
    serverUnreadCount.value = 0;
    loadError.value = null;
    listRecoveryAttempts = 0;
    countRecoveryAttempts = 0;
  }

  function patchLocalReadState(recipientId: DashboardNotification["recipient_id"], isRead: boolean) {
    latestPage.value = latestPage.value.map((notification) =>
      notification.recipient_id === recipientId
        ? {
            ...notification,
            is_read: isRead,
            read_at: isRead ? Date.now() : undefined,
          }
        : notification,
    );

    olderPages.value = olderPages.value.map((page) =>
      page.map((notification) =>
        notification.recipient_id === recipientId
          ? {
              ...notification,
              is_read: isRead,
              read_at: isRead ? Date.now() : undefined,
            }
          : notification,
      ),
    );
  }

  function patchAllRead() {
    latestPage.value = latestPage.value.map((notification) => ({
      ...notification,
      is_read: true,
      read_at: Date.now(),
    }));
    olderPages.value = olderPages.value.map((page) =>
      page.map((notification) => ({
        ...notification,
        is_read: true,
        read_at: Date.now(),
      })),
    );
  }

  function subscribeCount() {
    cleanupCountSubscription();

    if (!canUseNotifications() || unreadCountConsumerCount <= 0) {
      return;
    }

    const convex = getProjectsConvexClient();
    unsubscribeCount = convex.onUpdate(
      projectFns.getUnreadNotificationsCount,
      {},
      (result) => {
        serverUnreadCount.value = result;
        countRecoveryAttempts = 0;
      },
      (error: Error) => {
        console.error("[useNotifications] count subscription error", error);
        captureConvexError(error, {
          operation: "notifications.unread_count_subscription_failed",
          level: "warning",
          functionName: "notifications.countUnreadForViewer",
          auth: {
            isRecoverable: countRecoveryAttempts < 1,
            recoveryAttempt: countRecoveryAttempts,
          },
          counts: { notifications: notifications.value.length },
          extra: { viewerKey: currentViewerKey.value },
        });
        void recoverProjectsConvexSubscription({
          operation: "notifications.unread_count_subscription",
          functionName: "notifications.countUnreadForViewer",
          error,
          recoveryAttempt: countRecoveryAttempts,
        }).then((result) => {
          countRecoveryAttempts = result.nextRecoveryAttempt;
          if (result.didRecover) {
            subscribeCount();
            return;
          }
          const supportReference = captureConvexError(error, {
            operation: "notifications.unread_count_subscription_unrecovered",
            level: "error",
            functionName: "notifications.countUnreadForViewer",
            auth: { didRecover: false, recoveryAttempt: countRecoveryAttempts },
            extra: { viewerKey: currentViewerKey.value },
          });
          loadError.value = buildProjectUserError("loadNotifications", error, supportReference).description;
        });
      },
    );
  }

  function subscribeList() {
    cleanupListSubscription();

    if (!canUseNotifications()) {
      isReady.value = true;
      return;
    }

    const convex = getProjectsConvexClient();
    unsubscribeList = convex.onUpdate(
      projectFns.listNotifications,
      {
        paginationOpts: { numItems: PAGE_SIZE, cursor: null },
      },
      (result) => {
        loadError.value = null;
        latestPage.value = result.page;
        continueCursor.value = result.continueCursor;
        isDone.value = result.isDone;
        listVersion.value += 1;
        isReady.value = true;
        listRecoveryAttempts = 0;
      },
      (error: Error) => {
        console.error("[useNotifications] list subscription error", error);
        captureConvexError(error, {
          operation: "notifications.list_subscription_failed",
          level: "warning",
          functionName: "notifications.listForViewer",
          auth: {
            isRecoverable: listRecoveryAttempts < 1,
            recoveryAttempt: listRecoveryAttempts,
          },
          pagination: { pageSize: PAGE_SIZE, hasCursor: false },
          extra: { viewerKey: currentViewerKey.value },
        });
        void recoverProjectsConvexSubscription({
          operation: "notifications.list_subscription",
          functionName: "notifications.listForViewer",
          error,
          recoveryAttempt: listRecoveryAttempts,
        }).then((result) => {
          listRecoveryAttempts = result.nextRecoveryAttempt;
          if (result.didRecover) {
            subscribe();
            return;
          }
          isReady.value = true;
          listVersion.value += 1;
          const supportReference = captureConvexError(error, {
            operation: "notifications.list_subscription_unrecovered",
            level: "error",
            functionName: "notifications.listForViewer",
            auth: { didRecover: false, recoveryAttempt: listRecoveryAttempts },
            extra: { viewerKey: currentViewerKey.value },
          });
          loadError.value = buildProjectUserError("loadNotifications", error, supportReference).description;
        });
      },
    );
  }

  function subscribe() {
    cleanupSubscriptions();
    resetPages();

    if (!canUseNotifications()) {
      return;
    }

    subscribeCount();
    subscribeList();
  }

  function syncAuthState() {
    const nextViewerKey = toViewerKey(getConvexAuthInfo());
    const nextValue = nextViewerKey !== null;
    const viewerChanged = nextViewerKey !== currentViewerKey.value;

    currentViewerKey.value = nextViewerKey;
    if (nextValue && (!isEnabled.value || viewerChanged)) {
      isEnabled.value = true;
      subscribe();
      return;
    }
    if (!nextValue && isEnabled.value) {
      isEnabled.value = false;
      subscribe();
      return;
    }
    isEnabled.value = nextValue;
  }

  function ensureStarted() {
    syncAuthState();
    if (!unwatchAuthStore) {
      const authStore = useAuthStore();
      unwatchAuthStore = watch(
        () => [authStore.token, authStore.user?.id] as const,
        () => {
          syncAuthState();
        },
        { flush: "sync" },
      );
    }
    if (!authCheckInterval) {
      authCheckInterval = setInterval(syncAuthState, 2000);
    }
    if (!unsubscribeList && consumerCount > 0) {
      subscribeList();
    }
    if (!unsubscribeCount && unreadCountConsumerCount > 0) {
      subscribeCount();
    }
  }

  function retain(options: RetainNotificationsOptions = {}) {
    const wantsUnreadCount = options.subscribeToUnreadCount !== false;
    consumerCount += 1;
    if (wantsUnreadCount) {
      unreadCountConsumerCount += 1;
    }
    ensureStarted();

    return () => {
      consumerCount = Math.max(0, consumerCount - 1);
      if (wantsUnreadCount) {
        unreadCountConsumerCount = Math.max(0, unreadCountConsumerCount - 1);
      }

      if (consumerCount === 0) {
        cleanup();
        return;
      }

      if (unreadCountConsumerCount === 0) {
        cleanupCountSubscription();
        serverUnreadCount.value = 0;
      }
    };
  }

  async function loadMore() {
    if (!canUseNotifications() || isDone.value || isLoadingMore.value || !continueCursor.value) {
      return;
    }

    isLoadingMore.value = true;
    try {
      const result = await runProjectsConvexQuery(
        projectFns.listNotifications,
        { paginationOpts: { numItems: PAGE_SIZE, cursor: continueCursor.value } },
        {
          operation: "notifications.load_more",
          functionName: "notifications.listForViewer",
          onError: (error, context) => {
            captureConvexError(error, {
              operation: "notifications.load_more_failed",
              level: context.isRecoverable ? "warning" : "error",
              functionName: "notifications.listForViewer",
              auth: {
                isRecoverable: context.isRecoverable,
                recoveryAttempt: context.recoveryAttempt,
              },
              pagination: { pageSize: PAGE_SIZE, hasCursor: Boolean(continueCursor.value), isDone: isDone.value },
              counts: { notifications: notifications.value.length },
              extra: { viewerKey: currentViewerKey.value },
            });
          },
        },
      );
      if (result.page.length > 0) {
        olderPages.value = [...olderPages.value, result.page];
      }
      continueCursor.value = result.continueCursor;
      isDone.value = result.isDone;
    } catch (error) {
      console.error("[useNotifications] loadMore error", error);
      const supportReference = captureConvexError(error, {
        operation: "notifications.load_more_unrecovered",
        level: "error",
        functionName: "notifications.listForViewer",
        pagination: { pageSize: PAGE_SIZE, hasCursor: Boolean(continueCursor.value), isDone: isDone.value },
        counts: { notifications: notifications.value.length },
        extra: { viewerKey: currentViewerKey.value },
      });
      loadError.value = buildProjectUserError("loadNotifications", error, supportReference).description;
    } finally {
      isLoadingMore.value = false;
    }
  }

  watch(notifications, (list) => {
    if (dismissingRecipientIds.value.size === 0) return;
    const listed = new Set(list.map((notification) => notification.recipient_id));
    const next = new Set([...dismissingRecipientIds.value].filter((recipientId) => listed.has(recipientId)));
    if (next.size !== dismissingRecipientIds.value.size) dismissingRecipientIds.value = next;
  });

  function removeLocal(recipientId: DashboardNotification["recipient_id"]) {
    latestPage.value = latestPage.value.filter((notification) => notification.recipient_id !== recipientId);
    olderPages.value = olderPages.value.map((page) => page.filter((notification) => notification.recipient_id !== recipientId));
  }

  /** Drops a notification the viewer has already seen in place, instead of leaving it marked read. */
  async function dismiss(recipientId: DashboardNotification["recipient_id"]) {
    if (!canUseNotifications()) return;
    // Registered synchronously so the badge never renders the increment for a notification being dropped.
    dismissingRecipientIds.value = new Set(dismissingRecipientIds.value).add(recipientId);
    try {
      await runProjectsConvexMutation(
        projectFns.dismissNotification,
        { notification_recipient_id: recipientId },
        {
          operation: "notifications.dismiss",
          functionName: "notifications.dismiss",
          onError: (error, context) => {
            captureConvexError(error, {
              operation: "notifications.dismiss_failed",
              level: context.isRecoverable ? "warning" : "error",
              functionName: "notifications.dismiss",
              auth: { isRecoverable: context.isRecoverable, recoveryAttempt: context.recoveryAttempt },
              extra: { recipientId, viewerKey: currentViewerKey.value },
            });
          },
        },
      );
      removeLocal(recipientId);
    } catch (error) {
      const next = new Set(dismissingRecipientIds.value);
      next.delete(recipientId);
      dismissingRecipientIds.value = next;
      captureConvexError(error, {
        operation: "notifications.dismiss_unrecovered",
        level: "error",
        functionName: "notifications.dismiss",
        extra: { recipientId, viewerKey: currentViewerKey.value },
      });
      throw error;
    }
  }

  async function markRead(recipientId: DashboardNotification["recipient_id"]) {
    if (!canUseNotifications()) return;
    try {
      await runProjectsConvexMutation(
        projectFns.markNotificationRead,
        { notification_recipient_id: recipientId },
        {
          operation: "notifications.mark_read",
          functionName: "notifications.markRead",
          onError: (error, context) => {
            captureConvexError(error, {
              operation: "notifications.mark_read_failed",
              level: context.isRecoverable ? "warning" : "error",
              functionName: "notifications.markRead",
              auth: {
                isRecoverable: context.isRecoverable,
                recoveryAttempt: context.recoveryAttempt,
              },
              extra: { recipientId, viewerKey: currentViewerKey.value },
            });
          },
        },
      );
      patchLocalReadState(recipientId, true);
    } catch (error) {
      const supportReference = captureConvexError(error, {
        operation: "notifications.mark_read_unrecovered",
        level: "error",
        functionName: "notifications.markRead",
        extra: { recipientId, viewerKey: currentViewerKey.value },
      });
      loadError.value = buildProjectUserError("updateNotification", error, supportReference).description;
      throw error;
    }
  }

  async function markUnread(recipientId: DashboardNotification["recipient_id"]) {
    if (!canUseNotifications()) return;
    try {
      await runProjectsConvexMutation(
        projectFns.markNotificationUnread,
        { notification_recipient_id: recipientId },
        {
          operation: "notifications.mark_unread",
          functionName: "notifications.markUnread",
          onError: (error, context) => {
            captureConvexError(error, {
              operation: "notifications.mark_unread_failed",
              level: context.isRecoverable ? "warning" : "error",
              functionName: "notifications.markUnread",
              auth: {
                isRecoverable: context.isRecoverable,
                recoveryAttempt: context.recoveryAttempt,
              },
              extra: { recipientId, viewerKey: currentViewerKey.value },
            });
          },
        },
      );
      patchLocalReadState(recipientId, false);
    } catch (error) {
      const supportReference = captureConvexError(error, {
        operation: "notifications.mark_unread_unrecovered",
        level: "error",
        functionName: "notifications.markUnread",
        extra: { recipientId, viewerKey: currentViewerKey.value },
      });
      loadError.value = buildProjectUserError("updateNotification", error, supportReference).description;
      throw error;
    }
  }

  async function markAllRead() {
    if (!canUseNotifications()) return;
    try {
      await runProjectsConvexMutation(
        projectFns.markAllNotificationsRead,
        {},
        {
          operation: "notifications.mark_all_read",
          functionName: "notifications.markAllRead",
          onError: (error, context) => {
            captureConvexError(error, {
              operation: "notifications.mark_all_read_failed",
              level: context.isRecoverable ? "warning" : "error",
              functionName: "notifications.markAllRead",
              auth: {
                isRecoverable: context.isRecoverable,
                recoveryAttempt: context.recoveryAttempt,
              },
              counts: { notifications: notifications.value.length },
              extra: { viewerKey: currentViewerKey.value },
            });
          },
        },
      );
      patchAllRead();
    } catch (error) {
      const supportReference = captureConvexError(error, {
        operation: "notifications.mark_all_read_unrecovered",
        level: "error",
        functionName: "notifications.markAllRead",
        counts: { notifications: notifications.value.length },
        extra: { viewerKey: currentViewerKey.value },
      });
      loadError.value = buildProjectUserError("markAllNotificationsRead", error, supportReference).description;
      throw error;
    }
  }

  return {
    notifications,
    unreadCount,
    loadError,
    viewerKey: computed(() => currentViewerKey.value),
    listVersion: computed(() => listVersion.value),
    isReady: computed(() => isReady.value),
    isLoadingMore: computed(() => isLoadingMore.value),
    hasMore,
    isEnabled: computed(() => isEnabled.value),
    retain,
    loadMore,
    markRead,
    markUnread,
    markAllRead,
    dismiss,
    refresh: subscribe,
  };
});
